import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  Divider,
  IconButton,
  Menu,
  Tooltip,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  SupervisorAccount as SuperUserIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  VpnKey as PasswordIcon,
  Security as RoleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, CreateUserData, ApiResponse } from '../types';


const UserManagementPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Create User Dialog State
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [userFormData, setUserFormData] = useState<CreateUserData>({
    email: '',
    password: '',
    role: 'user',
  });
  const [createUserError, setCreateUserError] = useState<string>('');

  // Role Change Dialog State
  const [roleChangeOpen, setRoleChangeOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState<'admin' | 'user'>('user');
  const [roleChangeError, setRoleChangeError] = useState<string>('');

  // Password Change Dialog State
  const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState<string>('');

  // Delete User Dialog State
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [deleteUserError, setDeleteUserError] = useState<string>('');

  // User Actions Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUserId, setMenuUserId] = useState<number | null>(null);

  useEffect(() => {
    if (user?.role === 'superuser' || user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/users', {
        credentials: 'include',
      });
      const data: ApiResponse<User[]> = await response.json();
      
      if (data.success && data.data) {
        setUsers(data.data);
      } else {
        setError(data.message || 'Failed to fetch users');
      }
    } catch (error) {
      setError('Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setCreateUserError('');
      
      if (!userFormData.email || !userFormData.password) {
        setCreateUserError('Email and password are required');
        return;
      }

      if (userFormData.password.length < 6) {
        setCreateUserError('Password must be at least 6 characters');
        return;
      }

      const endpoint = userFormData.role === 'admin' ? '/api/users/admin' : '/api/users/user';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(userFormData),
      });

      const data: ApiResponse = await response.json();
      
      if (data.success) {
        setCreateUserOpen(false);
        setUserFormData({
          email: '',
          password: '',
          role: 'user',
        });
        fetchUsers(); // Refresh user list
      } else {
        setCreateUserError(data.message || 'Failed to create user');
      }
    } catch (error) {
      setCreateUserError('Error creating user');
    }
  };

  const handleToggleUserStatus = async (userId: number, isActive: boolean) => {
    try {
      const response = await fetch(`/api/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ isActive: !isActive }),
      });

      const data: ApiResponse = await response.json();
      
      if (data.success) {
        fetchUsers(); // Refresh user list
      } else {
        setError(data.message || 'Failed to update user status');
      }
    } catch (error) {
      setError('Error updating user status');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: number) => {
    setAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  const handleRoleChangeOpen = (userItem: User) => {
    setSelectedUser(userItem);
    setNewRole(userItem.role as 'admin' | 'user');
    setRoleChangeOpen(true);
    handleMenuClose();
  };

  const handlePasswordChangeOpen = (userItem: User) => {
    setSelectedUser(userItem);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordChangeOpen(true);
    handleMenuClose();
  };

  const handleDeleteUserOpen = (userItem: User) => {
    setSelectedUser(userItem);
    setDeleteUserError('');
    setDeleteUserOpen(true);
    handleMenuClose();
  };

  const handleRoleChange = async () => {
    if (!selectedUser) return;

    try {
      setRoleChangeError('');

      const response = await fetch(`/api/users/${selectedUser.id}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });

      const data: ApiResponse = await response.json();
      
      if (data.success) {
        setRoleChangeOpen(false);
        setSelectedUser(null);
        fetchUsers(); // Refresh user list
      } else {
        setRoleChangeError(data.message || 'Failed to update user role');
      }
    } catch (error) {
      setRoleChangeError('Error updating user role');
    }
  };

  const handlePasswordChange = async () => {
    if (!selectedUser) return;

    try {
      setPasswordChangeError('');

      if (!newPassword || newPassword.length < 6) {
        setPasswordChangeError('Password must be at least 6 characters');
        return;
      }

      if (newPassword !== confirmPassword) {
        setPasswordChangeError('Passwords do not match');
        return;
      }

      const response = await fetch(`/api/users/${selectedUser.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ newPassword }),
      });

      const data: ApiResponse = await response.json();
      
      if (data.success) {
        setPasswordChangeOpen(false);
        setSelectedUser(null);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordChangeError(data.message || 'Failed to change user password');
      }
    } catch (error) {
      setPasswordChangeError('Error changing user password');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setDeleteUserError('');

      const response = await fetch(`/api/users/${selectedUser.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data: ApiResponse = await response.json();
      
      if (data.success) {
        setDeleteUserOpen(false);
        setSelectedUser(null);
        fetchUsers(); // Refresh user list
      } else {
        setDeleteUserError(data.message || 'Failed to delete user');
      }
    } catch (error) {
      setDeleteUserError('Error deleting user');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'superuser':
        return <SuperUserIcon color="error" />;
      case 'admin':
        return <AdminIcon color="warning" />;
      default:
        return <UserIcon color="action" />;
    }
  };

  const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (role) {
      case 'superuser':
        return 'error';
      case 'admin':
        return 'warning';
      default:
        return 'default';
    }
  };




  if (user?.role !== 'superuser' && user?.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied. Only administrators can access user management.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            variant="outlined"
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            User Management
          </Typography>
        </Box>
        
        {(user?.role === 'superuser' || user?.role === 'admin') && (
          <Button
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/default-config')}
            variant="outlined"
            color="primary"
          >
            Default Template
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Create User Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Create New User Account</Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setCreateUserOpen(true)}
            size="large"
          >
            Create User
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {user?.role === 'superuser' 
            ? 'Create admin or regular user accounts. Admin users can create other admin and regular users.'
            : user?.role === 'admin'
            ? 'Create admin or regular user accounts for your organization.'
            : 'Create regular user accounts for your organization.'
          }
        </Typography>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* User List Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Users
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage existing user accounts and their active status.
        </Typography>

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading users...</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((userItem) => (
                  <TableRow key={userItem.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRoleIcon(userItem.role)}
                        {userItem.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={userItem.role} 
                        color={getRoleColor(userItem.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{userItem.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={userItem.is_active ? 'Active' : 'Inactive'} 
                        color={userItem.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(userItem.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {userItem.role !== 'superuser' && (
                          <Switch
                            checked={Boolean(userItem.is_active)}
                            onChange={() => handleToggleUserStatus(userItem.id, Boolean(userItem.is_active))}
                            size="small"
                          />
                        )}
                        {(user?.role === 'superuser' || user?.role === 'admin') && userItem.role !== 'superuser' && (
                          <Tooltip title="User Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, userItem.id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onClose={() => setCreateUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          {createUserError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createUserError}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={userFormData.email}
            onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
          />
          
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={userFormData.password}
            onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
          />
          
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={userFormData.role}
              label="Role"
              onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value as 'admin' | 'user' })}
            >
              <MenuItem value="user">Regular User</MenuItem>
              {(user?.role === 'superuser' || user?.role === 'admin') && (
                <MenuItem value="admin">Administrator</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const userItem = users.find(u => u.id === menuUserId);
          if (userItem) handleRoleChangeOpen(userItem);
        }}>
          <RoleIcon fontSize="small" sx={{ mr: 1 }} />
          Change Role
        </MenuItem>
        <MenuItem onClick={() => {
          const userItem = users.find(u => u.id === menuUserId);
          if (userItem) handlePasswordChangeOpen(userItem);
        }}>
          <PasswordIcon fontSize="small" sx={{ mr: 1 }} />
          Change Password
        </MenuItem>
        {(user?.role === 'superuser' || user?.role === 'admin') && (() => {
          const userItem = users.find(u => u.id === menuUserId);
          // Superuser can delete any non-superuser, Admin can delete admin and regular users
          if (user?.role === 'superuser') {
            return userItem && userItem.role !== 'superuser' && userItem.id !== user.id;
          } else if (user?.role === 'admin') {
            return userItem && userItem.role !== 'superuser' && userItem.id !== user.id;
          }
          return false;
        })() && (
          <MenuItem onClick={() => {
            const userItem = users.find(u => u.id === menuUserId);
            if (userItem) handleDeleteUserOpen(userItem);
          }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
            <Typography color="error.main">Delete Account</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Role Change Dialog */}
      <Dialog open={roleChangeOpen} onClose={() => setRoleChangeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          {roleChangeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {roleChangeError}
            </Alert>
          )}
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Changing role for: <strong>{selectedUser?.email}</strong>
          </Typography>

          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={newRole}
              label="Role"
              onChange={(e) => setNewRole(e.target.value as 'admin' | 'user')}
            >
              <MenuItem value="user">Regular User</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleChangeOpen(false)}>Cancel</Button>
          <Button onClick={handleRoleChange} variant="contained">
            Update Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordChangeOpen} onClose={() => setPasswordChangeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change User Password</DialogTitle>
        <DialogContent>
          {passwordChangeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordChangeError}
            </Alert>
          )}
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Changing password for: <strong>{selectedUser?.email}</strong>
          </Typography>

          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Password must be at least 6 characters"
          />

          <TextField
            margin="dense"
            label="Confirm Password"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordChangeOpen(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserOpen} onClose={() => setDeleteUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User Account</DialogTitle>
        <DialogContent>
          {deleteUserError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteUserError}
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the account for: <strong>{selectedUser?.email}</strong>?
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action cannot be undone. All user data, including their dashboards and configurations, will be permanently deleted.
          </Typography>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Warning:</strong> This is a permanent action that cannot be reversed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteUser} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default UserManagementPage;